import { InlineAlert } from '@travelnest/hui';
import { Rule } from '@travelnest/merlin.rule';
import { cloneDeep, isEqual } from 'lodash';
import * as React from 'react';
import { useEffect, useState } from 'react';
import api from '../../../../../api';
import { IPaymentLinkData } from '../../../../../api/availabilityV3';
import { IBookingStateEvent, ILatestVerificationState } from '../../../../../data/entities/booking-state';
import { ICommissionBreakdownObject } from '../../../../../data/entities/commission-breakdown';
import { ICustomer } from '../../../../../data/entities/customer';
import { IFinancial } from '../../../../../data/entities/customer/financial';
import { IDirectDiscounts } from '../../../../../data/entities/direct-discounts';
import { IEventV2 } from '../../../../../data/entities/event';
import { IPendingEventNote } from '../../../../../data/entities/event/event-note';
import { IGuestV2 } from '../../../../../data/entities/event/guest';
import { IExperiments } from '../../../../../data/entities/experiments';
import { IPendingPostPayment } from '../../../../../data/entities/post-payment';
import { IPromotionOpportunity } from '../../../../../data/entities/promotions';
import { IProperty } from '../../../../../data/entities/property';
import { UserGroups } from '../../../../../entities/auth/constants';
import { ActionTypes } from '../../../../../entities/bookings/actions';
import { logging } from '../../../../../logging';
import { SaveState } from '../../../../../types';
import { ActionTile } from '../../../../components/action-tile/ActionTile';
import { ConfirmBookingModal } from '../../../../components/booking-confirm/ConfirmBookingModal';
import { getBookingStatusDisplay } from '../../../../components/booking-list/helpers';
import { IPendingBookingReceipt } from '../../../../components/booking-payment/helpers';
import { ProcessPaymentModal } from '../../../../components/booking-payment/PaymentRequestModal';
import { GuestBooking } from '../../../../components/booking-view/GuestBooking';
import { GuestDetails } from '../../../../components/booking-view/GuestDetails';
import { InternalBookingDetails } from '../../../../components/booking-view/InternalBookingDetails';
import { OwnerFinanceBreakdown } from '../../../../components/booking-view/OwnerFinanceBreakdown';
import { ContentHeader } from '../../../../components/content-header/ContentHeaderContainer';
import { Loader } from '../../../../components/loader/Loader';
import { NavTile } from '../../../../components/nav-tile/NavTile';
import { Content } from '../../../../components/page';
import { Panel } from '../../../../components/panel/Panel';
import { SaveButton } from '../../../../components/save-button/SaveButton';
import { SpanAnchorLink } from '../../../../components/span-anchor-link/SpanAnchorLink';
import { currencySymbolForCurrencyString } from '../../../../helpers/currency_helpers';
import { useReduxAction } from '../../../../helpers/hooks';
import { displayAutomationProcesses, failureStateEventMappings } from './automation-process/helpers';
import { ProcessBookingModal } from './automation-process/ProcessBookingModal';
import {
	getTouristTaxNoticeContent,
	getTouristTaxType,
	IFXRates,
	isBookingConfirmed,
	isVCCBooking,
	sortArraysInBookingObject,
	TaxSubType,
	touristTaxAlertContent,
} from './helpers/helpers';
import { chargeHostCancellation, isChargeHostCancellationEnabled } from './helpers/hostCancellation';
import { CancelBookingModal } from './modals/CancelBookingModal';
import { CancellationRequestModal } from './modals/CancellationRequestModal';
import { CancelPaymentRemindersModal } from './modals/CancelPaymentRemindersModal';
import { ChargeHostCancellationModal } from './modals/ChargeHostCancellationModal';
import { ExtendPaymentRemindersModal } from './modals/ExtendPaymentRemindersModal';
import { RefreshBookingModal } from './modals/RefreshBookingModal';
import { SetCurrencyModal } from './modals/SetCurrencyModal';
import { TransferBookingModal } from './modals/TransferBookingModal';

interface IPropertyBookingsProps {
	property: IProperty;
	customer: ICustomer;
	customerExperiments: IExperiments;
	financial: IFinancial;
	event: IEventV2;
	promotionOpportunities: IPromotionOpportunity[];
	firstBooking: boolean;
	loading: boolean;
	saving: SaveState;
	admin: boolean;
	userGroups: UserGroups[];
	cancelling: { [key: string]: boolean; };
	commissionBreakdown: ICommissionBreakdownObject;
	directDiscounts: IDirectDiscounts[];
	pendingPostPayments: IPendingPostPayment[];
	fxRates: IFXRates;
	pendingEventNotes: IPendingEventNote[];
	bookingStateEvents: IBookingStateEvent[];
	latestVerificationState: ILatestVerificationState;
	submitOverridesSaveState: SaveState;
	paymentLink: IPaymentLinkData;
	cancelBooking: (propertyId: string, eventId: string) => any;
	transferBooking: (propertyId: string, eventId: string) => any;
	updateBooking: (propertyId: string, eventId: string, event: IEventV2) => any;
	auditUpdates: (propertyId: string, eventId: string, event: IEventV2) => any;
	submitPostPayments: (propertyId: string, eventId: string, postPayments: IPendingPostPayment[]) => any;
	submitEventNotes: (propertyId: string, eventId: string, eventNotes: IPendingEventNote[]) => any;
	getRUCurrency: (propertyId: string, eventId: string, rentalsunitedBookingId?: string, currency?: string) => any;
	requestBookingPayment: (propertyId: string, eventId: string) => any;
	submitOverrides: (propertyId: string, eventId: string, overrides: string[]) => any;
}

const acceptedChannelsForPayment = ['booking.com', 'homeaway.co.uk', 'expedia.com', 'direct'];

const wrapPageContent = (content: any) => <><Content type="mid">{content}</Content></>;

interface IRUCurrencyRefreshProps {
	event: IEventV2;
	getRUCurrency: (propertyId: string, eventId: string, rentalsunitedBookingId?: string) => any;
}

const RUCurrencyRefreshComponent: React.FC<IRUCurrencyRefreshProps> = (props) => {
	return <ActionTile
		label="Update currency"
		loadingLabel="Updating currency&hellip;"
		onClick={async () => {
			try {
				await props.getRUCurrency(
					props.event.property_id,
					props.event.id,
					props.event.booking?.external_id?.[0]?.id
				);

				return 'Updated currency';
			} catch (e) {
				throw new Error('Unable to update currency');
			}
		}}
	/>;
};

export const PropertyBookingsComponent = (props: IPropertyBookingsProps) => {
	// Modals
	const [showCancelModal, setShowCancelModal] = useState(false);
	const [showProcessBookingModal, setShowProcessBookingModal] = useState(false);
	const [showSetCurrencyModal, setShowSetCurrencyModal] = useState(false);
	const [showCancellationRequestModal, setShowCancellationRequestModal] = useState(false);
	const [showPaymentModal, setShowPaymentModal] = useState(false);
	const [showConfirmBookingModal, setShowConfirmBookingModal] = useState(false);
	const [showTransferModal, setShowTransferModal] = useState(false);
	const [showRefreshModal, setShowRefreshModal] = useState(false);
	const [showChargeHostCancellationModal, setShowChargeHostCancellationModal] = useState(false);
	const [showExtendPaymentRemindersModal, setShowExtendPaymentRemindersModal] = useState(false);
	const [showCancelPaymentRemindersModal, setShowCancelPaymentRemindersModal] = useState(false);

	// Booking details
	const [event, setEventInternal] = useState<IEventV2>(props.event);
	const [originalEvent, setOriginalEvent] = useState<IEventV2>(props.event);
	const bookingDisplayStatus = getBookingStatusDisplay(props.event);
	const bookingConfirmed = isBookingConfirmed(props.event, props.bookingStateEvents);
	const commissionBreakdown = props.commissionBreakdown;
	const [verificationFailures, setVerificationFailures] = useState(props.latestVerificationState.failures ?? {});
	const touristTax = event.booking?.value?.breakdown?.owner?.converted_tourist_tax;
	const touristTaxType = getTouristTaxType(event) as TaxSubType;
	const currency = event.booking?.value?.currency;
	const isUsingPaymentLink = event.booking?.using_payment_link;

	// Tracking changes
	const [hasPending, setHasPending] = useState(false);
	const [preUpdateSaving, setPreUpdateSaving] = useState(false);

	const setEvent = (e: IEventV2, setPendingState = false) => {
		if (setPendingState) {
			setHasPending(true);
		}

		setEventInternal(e);
	};

	useEffect(() => {
		if (props.event && props.event.id) {
			document.title = `${props.event.id} - Travelnest Booking`;
		}
		setEvent(props.event);
		setVerificationFailures(props.latestVerificationState.failures);
	}, [props.loading, props.saving]);

	useEffect(() => {
		setOriginalEvent(cloneDeep(props.event));
	}, [props.event]);

	useReduxAction(ActionTypes.BOOKINGV2_UPDATE_RECEIVE, (action) => setHasPending(false));

	const removeSoftDeletedReceipts = () => {
		const ownerPayments = [...(event.booking?.owner_payments?.payments || [])]
			.filter((receipt: IPendingBookingReceipt) => !receipt.deleted);

		const guestReceipts = [...(event.booking?.guest_receipts?.receipts || [])]
			.filter((receipt: IPendingBookingReceipt) => !receipt.deleted);

		const newEvent = { ...event };
		if (newEvent?.booking?.owner_payments) {
			newEvent.booking.owner_payments.payments = ownerPayments;
		}

		if (newEvent?.booking?.guest_receipts) {
			newEvent.booking.guest_receipts.receipts = guestReceipts;
		}

		return newEvent;
	};

	const sendBookingUpdate = async () => {
		const latestEvent = await api.bookings.getBookingById(event.property_id, event.id);

		// The endpoints can return the same data in different order, so we need to sort the arrays in the object
		const latestEventSorted = sortArraysInBookingObject(latestEvent);
		const originalEventSorted = sortArraysInBookingObject(originalEvent);

		if (!isEqual(latestEventSorted, originalEventSorted)) {
			setShowRefreshModal(true);
			return;
		}

		try {
			setPreUpdateSaving(true);

			await props.auditUpdates(event.property_id, event.id, event);
			await props.submitPostPayments(event.property_id, event.id, props.pendingPostPayments);
			await props.submitEventNotes(event.property_id, event.id, props.pendingEventNotes);

			setEvent(removeSoftDeletedReceipts());

			props.updateBooking(event.property_id, event.id, event);
		} catch (e) {
			logging.error('Failed to save booking', e);
			throw e;
		} finally {
			setPreUpdateSaving(false);
		}
	};

	const setBookingState = (next: IEventV2) => setEvent(next, true);

	const setGuestState = (guest: IGuestV2) => {
		const next = { ...event };
		if (next.booking) {
			next.booking.guest = guest;
		}
		setBookingState(next);
	};

	// not sure if we want to allow payment charges for RU:HA bookings?
	// also probably want to gate on checking if there is any payment remaining...
	const canProcessPayment = acceptedChannelsForPayment.some((channel) => props.event?.source?.includes(channel));

	const currencySymbol = currency && currencySymbolForCurrencyString(currency);
	const touristTaxValue = `${currencySymbol}${touristTax}`;
	const contentAdmin = touristTaxType && getTouristTaxNoticeContent(touristTaxAlertContent[touristTaxType].CONTENT_ADMIN, touristTaxValue);
	const contentHost = touristTaxType && getTouristTaxNoticeContent(touristTaxAlertContent[touristTaxType].CONTENT_HOST, touristTaxValue);

	const touristTaxNotice = props.admin ? contentAdmin : contentHost;

	return (props.loading || !event.booking)
		? wrapPageContent(<Loader loading={true} />)
		: (
			<div style={props.admin ? { display: 'flex', flexWrap: 'wrap' } : {}}>
				<Content type="mid" className="property-bookings__container">
					<ContentHeader
						backLocation={`/property/${event.property_id}/bookings`}
						title="Guest booking"
						subtext={`Reference: ${event.id}`}
						sticky={true}
						childrenRhs={props.admin ?
							<div className="admin-booking__button">
								<SaveButton
									saving={preUpdateSaving || props.saving === SaveState.PENDING}
									buttonLabel="Save changes"
									successMessage="Your changes have been saved!"
									errorMessage="There was an issue saving your changes."
									saveState={props.saving}
									onClick={sendBookingUpdate}
									badge={hasPending ? 'Unsaved changes' : ''}
								/>
							</div> : <></>
						}
					/>

					{props.event.status === 'cancelled' &&
						<Panel withIcon={true} type="warn">
							This booking has been cancelled
						</Panel>
					}

					{props.event.status === 'transferred' &&
						<Panel withIcon={true} type="warn">
							This booking has been transferred to a customer's listing
						</Panel>
					}

					{!['cancelled', 'transferred'].includes(props.event.status) && verificationFailures && displayAutomationProcesses(event) &&
						<Panel withIcon={true} type="warn">
							This booking failed automated processing for the following reasons:
							<ul>
								{
									Object.keys(verificationFailures)
										.map((failure: keyof typeof failureStateEventMappings, i) => failureStateEventMappings[failure] &&
											<li key={i}>{failureStateEventMappings[failure].display}</li>)
								}
							</ul>
						</Panel>
					}

					{props.admin && props.firstBooking &&
						<Panel className="admin-booking__first-booking-message" withIcon={true} type="tada">
							This is this customer's first booking, make a big deal about it!
						</Panel>
					}
					{props.admin && isVCCBooking(props.event) &&
						<Panel className="vcc-panel" withIcon={true} type="default">
							This booking is a Virtual Credit Card (VCC) booking
						</Panel>
					}
					{!!touristTax && !!touristTaxType && touristTaxAlertContent[touristTaxType] &&
						<InlineAlert
							variant="informative"
							heading="Notice: Tourist Tax Collection"
							content={touristTaxNotice}
						/>
					}
					<GuestBooking
						event={event}
						property={props.property}
						customer={props.customer}
						admin={props.admin}
						onChange={setBookingState}
					/>
					{(props.admin || bookingDisplayStatus === 'Booked') && <GuestDetails
						admin={props.admin}
						event={event}
						customerId={props.customer.id}
						guest={event.booking && event.booking.guest ? event.booking.guest : {} as IGuestV2}
						source={props.event.source}
						onChange={setGuestState}
						paymentLink={props.paymentLink}
					/>}
				</Content>
				{props.admin && (
					<React.Fragment>
						{showConfirmBookingModal && <ConfirmBookingModal
							event={event}
							onRequestClose={() => setShowConfirmBookingModal(false)}
						/>
						}
						{showCancelModal && <CancelBookingModal
							propertyId={event.property_id}
							eventId={event.id}
							onRequestClose={() => setShowCancelModal(false)}
							cancelBooking={() => props.cancelBooking(event.property_id, event.id)}
							submitEventNotes={props.submitEventNotes}
						/>
						}
						{showProcessBookingModal && <ProcessBookingModal
							propertyId={event.property_id}
							eventId={event.id}
							verificationFailures={verificationFailures}
							onRequestClose={() => setShowProcessBookingModal(false)}
							submitOverridesSaveState={props.submitOverridesSaveState}
							submitOverrides={props.submitOverrides}
						/>
						}
						{showPaymentModal && <ProcessPaymentModal
							event={event}
							process={props.requestBookingPayment}
							onRequestClose={() => setShowPaymentModal(false)}
						/>
						}
						{showSetCurrencyModal && <SetCurrencyModal
							event={event}
							onRequestClose={() => setShowSetCurrencyModal(false)}
							sendCurrency={props.getRUCurrency}
						/>
						}
						{showCancellationRequestModal && <CancellationRequestModal
							customerId={props.customer.id}
							propertyId={event.property_id}
							eventId={event.id}
							onRequestClose={() => window.location.reload()}
							submitEventNotes={props.submitEventNotes}
						/>
						}
						{showChargeHostCancellationModal &&
						<ChargeHostCancellationModal
							onRequestClose={() =>
								setShowChargeHostCancellationModal(false)
							}
							chargeHostCancellation={() => chargeHostCancellation(
								event,
								props.customer.id,
								props.financial,
								props.submitPostPayments,
								props.submitEventNotes
							)}
						/>
						}
						{showTransferModal && <TransferBookingModal
							propertyId={event.property_id}
							eventId={event.id}
							onRequestClose={() => setShowTransferModal(false)}
							transferBooking={() => props.transferBooking(event.property_id, event.id)}
							submitEventNotes={props.submitEventNotes}
						/>
						}
						{showExtendPaymentRemindersModal &&
							<ExtendPaymentRemindersModal
								cancellationDate={props.paymentLink?.cancellation_date}
								startOn={event.start_on}
								checkInTime={props.property.check_in}
								onRequestClose={() => setShowExtendPaymentRemindersModal(false)}
								extendPaymentReminders={async (deferralHours: number) => {
									await api.availabilityV3.extendPaymentReminders(
										event.property_id.toString(),
										event.id,
										deferralHours
									);
									window.location.reload();
								}}
							/>
						}
						{showCancelPaymentRemindersModal &&
							<CancelPaymentRemindersModal
								onRequestClose={() => setShowCancelPaymentRemindersModal(false)}
								cancelPaymentReminders={async () => {
									await api.availabilityV3.cancelPaymentReminders(
										event.id
									);
									return 'Cancelled payment reminders';
								}}
							/>
						}
						{showRefreshModal && <RefreshBookingModal
							onRequestClose={() => setShowRefreshModal(false)}
						/>
						}
						<div className="home-right">
							<Content type="tile">
								<h2>Navigate</h2>
								<Rule />
								<SpanAnchorLink hash="breakdown" text="Booking breakdown" />
								<br />
								<SpanAnchorLink hash="notes" text="Notes" />
								<br />
								<SpanAnchorLink hash="receipts" text="Receipts" />
								<br />
								<SpanAnchorLink hash="payments" text="Payments" />
								<br />
								<SpanAnchorLink hash="booking-state-events" text="Booking state events" />
							</Content>
							<Content type="tile">
								<h2>Related actions</h2>
								<Rule />
								{displayAutomationProcesses(event) && <ActionTile
									label="Process booking"
									loadingLabel="Processing booking&hellip;"
									disabled={props.latestVerificationState.verification_state === 'PASSED' && props.event.status === 'cancelled'}
									onClick={async (e?: any) => {
										try {
											setShowProcessBookingModal(true);
											return 'Requesting';
										} catch (e) {
											throw new Error('Unable to process booking');
										}
									}}
								/>}
								{!!event.source && <ActionTile
									label="Confirm booking"
									loadingLabel="Confirming booking&hellip;"
									disabled={bookingConfirmed}
									onClick={async (e?: any) => {
										try {
											setShowConfirmBookingModal(true);
											return 'Confirming';
										} catch (e) {
											throw new Error('Unable to confirm booking');
										}
									}}
								/>}
								<ActionTile
									label="Cancel booking"
									loadingLabel="Cancelling booking&hellip;"
									disabled={!['booked', 'transferred'].includes(event.status)}
									onClick={async (e?: any) => {
										try {
											setShowCancelModal(true);
											return 'Requesting';
										} catch (e) {
											throw new Error('Unable to cancel booking');
										}
									}}
								/>
								<ActionTile
									label="Cancellation request"
									disabled={!['Booked', 'Transferred'].includes(bookingDisplayStatus)}
									loadingLabel="Requesting cancellation&hellip;"
									onClick={async (e?: any) => {
										try {
											setShowCancellationRequestModal(true);
											return 'Requesting';
										} catch (e) {
											throw new Error('Unable to add cancellation request');
										}
									}}
								/>
								<ActionTile
								label="Charge Host Cancellation"
								disabled={!isChargeHostCancellationEnabled(event, props.customerExperiments)}
								loadingLabel="Launching Charge Host Cancellation Modal&hellip;"
								onClick={async (e?: any) => {
									try {
										setShowChargeHostCancellationModal(
											true
										);
										return 'Requesting';
									} catch (e) {
										throw new Error(
											'Unable to charge host cancellation'
										);
									}
								}}
							/>
								<ActionTile
									label="Process payment"
									loadingLabel="Processing payment&hellip;"
									disabled={!canProcessPayment || hasPending}
									onClick={async (e?: any) => {
										try {
											setShowPaymentModal(true);
											return 'Requesting';
										} catch (e) {
											throw new Error('Unable to process payment');
										}
									}}
								/>
								{
									isUsingPaymentLink &&
									<ActionTile
										label="Extend payment reminders"
										loadingLabel="Showing payment reminders&hellip;"
										disabled={event.status !== 'pending'}
										onClick={async (e?: any) => {
											try {
												setShowExtendPaymentRemindersModal(true);
												return 'Requesting';
											} catch (e) {
												throw new Error('Unable to extend payment reminders');
											}
										}}
								/>
}
								<ActionTile
									label="Cancel payment reminders"
									loadingLabel="Cancelling payment reminders&hellip;"
									disabled={!isUsingPaymentLink}
									onClick={async (e?: any) => {
										try {
											setShowCancelPaymentRemindersModal(true);
											return 'Requesting';
										} catch (e) {
											throw new Error('Unable to cancel payment reminders');
										}
									}}
								/>
								<ActionTile
									label="Virtual Terminal"
									loadingLabel="Redirecting to Virtual Terminal&hellip;"
									onClick={async (e?: any) => {
										try {
											window.location.assign(`/admin/property/${event.property_id}/booking/${event.id}/payment`);
											return 'Requesting';
										} catch (e) {
											throw new Error('Unable to redirect to Virtual Terminal');
										}
									}}
								/>
								<ActionTile
									label="Transfer booking"
									loadingLabel="Transferring booking&hellip;"
									disabled={event.status !== 'booked'}
									onClick={async (e?: any) => {
										try {
											setShowTransferModal(true);
											return 'Requesting';
										} catch (e) {
											throw new Error('Unable to transfer booking');
										}
									}}
								/>
								{props.event.booking?.value.currency === 'XTS' &&
									<RUCurrencyRefreshComponent event={event} getRUCurrency={props.getRUCurrency} />
								}
								{props.event.booking?.value.currency === 'XTS' &&
									<ActionTile
										label="Set Currency"
										loadingLabel="Setting currency&hellip;"
										onClick={async (e?: any) => {
											try {
												setShowSetCurrencyModal(true);
												return 'Requesting';
											} catch (e) {
												throw new Error('Unable to set currency for booking');
											}
										}}
									/>
								}
								<Rule />
								<NavTile
									label="View pricing"
									to={`/admin/property/${props.property.id}/pricing`} />
								<NavTile
									label="View owner details"
									to={`/admin/customer/${props.property.owner_id}/edit`} />
								<NavTile
									label="View property as owner"
									to={`/redirect/property/${props.property.id}/overview`} />
							</Content>
						</div>
					</React.Fragment>
				)}
				{
					!props.admin ?
						<OwnerFinanceBreakdown
							event={event}
							commissionBreakdown={commissionBreakdown} /> :
						<InternalBookingDetails
							event={event}
							commissionBreakdown={commissionBreakdown}
							property={props.property}
							customer={props.customer}
							loading={props.loading}
							setPending={setHasPending}
							setBookingState={setBookingState}
							sendBookingUpdate={sendBookingUpdate}
							fxRates={props.fxRates}
							bookingStateEvents={props.bookingStateEvents}
						/>
				}

			</div>
		);
	// tslint:disable-next-line: max-file-line-count
};
